<template>
  <div class="auth-wrap">
    <v-card class="auth-card" outlined>
      <!-- Logo / Title -->
      <div class="auth-header">
        <div class="brand" @click="home">
          <span class="brand-a">taleem</span><span class="brand-b">.app</span>
        </div>
        <div class="subtitle">Sign in to continue</div>
      </div>

      <v-card-text class="px-5 pt-4 pb-2">
        <!-- Code -->
        <v-text-field
          v-model.trim="code"
          outlined
          dense
          ref="locationCode"
          :error-messages="codeErrors"
          label="Code"
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          prepend-inner-icon="mdi-domain"
          @keyup.enter="focusEmail"
        />

        <!-- Forgot link (kept close to code, but not inside the field) -->
        <div class="d-flex align-center justify-end mt-n2 mb-3">
          <v-btn
            small
            text
            class="link-btn"
            @click="$router.push({ path: '/forgot' })"
          >
            Forgot code?
          </v-btn>
        </div>

        <!-- Email -->
        <v-text-field
          v-model.trim="email"
          outlined
          dense
          :error-messages="emailErrors"
          label="Email"
          autocomplete="email"
          inputmode="email"
          prepend-inner-icon="mdi-email-outline"
          @keyup.enter="focusPassword"
        />

        <!-- Password -->
        <v-text-field
          v-model="password"
          outlined
          dense
          :error-messages="passwordErrors"
          label="Password"
          :type="showPassword ? 'text' : 'password'"
          :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          prepend-inner-icon="mdi-lock-outline"
          autocomplete="current-password"
          @click:append="showPassword = !showPassword"
          @input="$v.password.$touch()"
          @keyup.enter="submit"
        />

        <div class="d-flex align-center justify-space-between mt-1">
          <v-btn
            text
            small
            class="link-btn px-0"
            @click="$router.push({ path: '/forgot' })"
          >
            Forgot password?
          </v-btn>

          <v-chip
            v-if="code"
            small
            class="code-chip"
            label
          >
            {{ code }}
          </v-chip>
        </div>

        <!-- Login button -->
        <v-btn
          class="mt-5"
          block
          large
          :loading="loading"
          color="primary"
          @click="submit"
        >
          {{ $t("buttons.login") }}
        </v-btn>

        <!-- Signup -->
        <v-btn
          class="mt-2"
          block
          text
          color="secondary"
          @click="$router.push({ path: '/signup' })"
        >
          Don’t have an account?
        </v-btn>
      </v-card-text>

      <v-divider />

      <v-card-text class="py-3 px-5 bottom-note">
        Secure sign-in • Powered by Taleem
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import authService from "../service";

const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");

export default {
  name: "authentication-login",
  mixins: [validationMixin],
  data() {
    return {
      showPassword: false,
      email: null,
      loading: false,
      password: null,
      code: null,
    };
  },
  created() {
    const localStoreCode = localStorage.getItem(
      `${process.env.VUE_APP_STORAGE_PREFIX}code`
    );
    if (localStoreCode) this.code = localStoreCode;
  },
  mounted() {
    this.$nextTick(() => {
      // mobile-friendly focus
      if (!this.code && this.$refs.locationCode) this.$refs.locationCode.focus();
    });
  },
  validations: {
    email: { email, required },
    code: { required },
    password: { required },
  },
  computed: {
    codeErrors() {
      const errors = [];
      if (!this.$v.code.$dirty) return errors;
      if (!this.$v.code.required) errors.push("Code is required.");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      if (!this.$v.email.email) errors.push("Email is not valid");
      if (!this.$v.email.required) errors.push("Email is required.");
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;
      if (!this.$v.password.required) errors.push("Password is required.");
      return errors;
    },
  },
  methods: {
    ...mapActions("global", ["initBusiness", "initUser"]),

    home() {
      window.open("https://taleem.com", "_self");
    },

    focusEmail() {
      // If you have refs for email/password later, hook them up here.
      // Keeping simple to avoid breaking current code.
    },

    focusPassword() {},

    checkResponse(response) {
      if (response.status === 400) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({ icon: "error", title: "Invalid User" });
      } else {
        localStorage.setItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}code`,
          this.code
        );
        this.$router.push({ name: "home" });
      }
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
        return;
      }

      this.loading = true;
      this.$Progress.start();

      authService
        .authenticate({
          email: this.email,
          password: this.password,
          code: this.code,
        })
        .then((response) => {
          this.$Progress.finish();
          this.checkResponse(response);
          this.loading = false;
        })
        .catch((error) => {
          this.$Progress.finish();
          this.loading = false;
          this.$swal("Invalid Login", error?.data?.message || "Try again", "error");
        });
    },
  },
};
</script>

<style scoped>
.auth-wrap {
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Match the nicer outer screen */
.auth-card {
  width: 100%;
  max-width: 380px;
  border-radius: 18px;
  overflow: hidden;

  /* if your outer container is already glassy, keep this simple */
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
}

.auth-header {
  padding: 18px 20px 10px 20px;
  text-align: center;
}

.brand {
  cursor: pointer;
  font-size: 40px;
  line-height: 1;
  user-select: none;
}
.brand-a {
  color: #50e3c2;
  font-weight: 600;
}
.brand-b {
  color: #2e2e2e;
  font-weight: 900;
}

.subtitle {
  margin-top: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  font-weight: 600;
}

.link-btn {
  text-transform: none;
  letter-spacing: 0;
}

.code-chip {
  background: rgba(80, 227, 194, 0.12);
  color: #0f6b4a;
  font-weight: 700;
}

.bottom-note {
  text-align: center;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  font-weight: 600;
}

/* Mobile polish */
@media (max-width: 420px) {
  .auth-card {
    max-width: 340px;
  }
  .brand {
    font-size: 36px;
  }
}
</style>